import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { IJobPositionGroupModel } from 'src/app/competence/model/job-position-group.model';
import { JobPositionGroupState } from 'src/app/competence/state/job-position-group.state';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { INonConformanceModel } from '../models/non-conformance.model';
import { IUserResponsibility } from '../models/user-responsibility.model';

@Injectable({
  providedIn: 'root',
})
export class NonConformanceHelperService {
  constructor(private store: Store) {}

  checkIfCurrentUserResponsible(nc: INonConformanceModel): Observable<IUserResponsibility> {
    const isProductOwner = this.store.selectSnapshot(TenantUserAccessState.isProductOwner) ?? false;
    const positionId =
      this.store.selectSnapshot(TenantUserAccessState.currentTenant).position?.id ?? '';
    const userRoles = this.store.selectSnapshot(
      TenantUserAccessState.getUserRolesForCurrentlySelectedDepartment
    );
    const userId = this.store.selectSnapshot(TenantUserAccessState.userAccess).id ?? '';
    const pgResponsibleForClosing = nc.positionGroupsResponsibleForClosing.map(({ id }) => id);
    const pgResponsibleForFollowUp = nc.positionGroupsResponsibleForFollowUp.map(({ id }) => id);
    const isPositionResponsibleForClosing = nc.positionsResponsibleForClosing.some(
      ({ id }) => id === positionId
    );
    const isPositionResponsibleForFollowUp = nc.positionsResponsibleForFollowUp.some(
      ({ id }) => id === positionId
    );
    const isUserResponsibleForClosing = nc.usersResponsibleForClosing.some(
      ({ id }) => id === userId
    );
    const isUserResponsibleForFollowUp = nc.usersResponsibleForFollowUp.some(
      ({ id }) => id === userId
    );
    const isUserRoleRespForClosing = nc.rolesResponsibleForClosing.some(({ id }) =>
      userRoles.includes(id)
    );
    const isUserRoleRespForFollowUp = nc.rolesResponsibleForFollowUp.some(({ id }) =>
      userRoles.includes(id)
    );

    if (
      pgResponsibleForClosing.length ||
      pgResponsibleForFollowUp.length ||
      isUserRoleRespForClosing ||
      isUserRoleRespForFollowUp
    ) {
      const positionGroups = this.store.selectSnapshot(
        JobPositionGroupState.getAllJobPositionGroupsWithPositions
      );
      const isPositionInGroup = (pg: IJobPositionGroupModel) =>
        pg.positions.some(({ id }) => id === positionId);
      const isUserPositionInGroupResponsibleForClosing = !!positionGroups
        ?.filter((pg) => pgResponsibleForClosing.includes(pg.id))
        .some(isPositionInGroup);
      const isUserPositionInGroupResponsibleForFollowUp = !!positionGroups
        ?.filter((pg) => pgResponsibleForFollowUp.includes(pg.id))
        .some(isPositionInGroup);
      const isResponsibleForClosing =
        isPositionResponsibleForClosing ||
        isUserResponsibleForClosing ||
        isUserRoleRespForClosing ||
        isUserPositionInGroupResponsibleForClosing;
      const isResponsibleForFollowUp =
        isPositionResponsibleForFollowUp ||
        isUserResponsibleForFollowUp ||
        isUserRoleRespForFollowUp ||
        isUserPositionInGroupResponsibleForFollowUp;
      return of({
        isResponsibleForFollowUp,
        isResponsibleForClosing,
        isResponsible: isProductOwner || isResponsibleForFollowUp || isResponsibleForClosing,
      });
    }
    const userResponsibility: IUserResponsibility = {
      isResponsibleForFollowUp:
        isPositionResponsibleForFollowUp ||
        isUserResponsibleForFollowUp ||
        isUserRoleRespForFollowUp,
      isResponsibleForClosing:
        isPositionResponsibleForClosing || isUserResponsibleForClosing || isUserRoleRespForClosing,
      isResponsible: false,
    };
    userResponsibility.isResponsible =
      isProductOwner ||
      userResponsibility.isResponsibleForFollowUp ||
      userResponsibility.isResponsibleForClosing;

    return of(userResponsibility);
  }
}
